/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { isNil } from 'lodash';
import SmoothScroll from 'smooth-scroll';
import { PopUpPosition } from 'src/types';
import styles from './dom-utils.module.scss';
import { isElement, isMouseEvent } from './types';
import { coalesce } from './utils';

export const POPUP_POSITION_TOP = PopUpPosition.TOP;
export const POPUP_POSITION_BOTTOM = PopUpPosition.BOTTOM;
export const TOOLTIP_POSITION_TOP = PopUpPosition.TOP;
export const TOOLTIP_POSITION_BOTTOM = PopUpPosition.BOTTOM;

export function alwaysCancelEvent(event: Event): void {
  if (event) {
    event.preventDefault();
  }
}

/**
 * Find a parent element that has a particular css class.
 *
 * @param {Node} sourceElement
 * @param {string} className
 */
export function findParentWithClass(
  sourceElement: HTMLElement,
  className: string
): HTMLElement {
  let el = sourceElement;
  while (!el?.classList?.contains(className)) {
    el = el.parentNode as HTMLElement;
  }
  return el;
}

/**
 * Tests if one element is within another (useful for outside click detection)
 * @param {HTMLElement} testParent
 * @param {HTMLElement} child
 * @return {boolean}
 */
export function isDescendant(testParent, child) {
  if (isNil(testParent)) {
    return false;
  }
  let node = child;
  while (node !== null) {
    if (node === testParent) {
      return true;
    }
    node = node?.parentNode;
  }
  return false;
}

export function isPhone(): boolean {
  return window.matchMedia(`(max-width: ${styles['screen-xs-max']})`).matches;
}

/**
 * Logically scroll an element into view, aligning top or bottom as appropriate based
 * on current position and visibility.
 *
 * @param {Node|string} elementOrIdOrClass Id of the html element to scroll into view.
 * @param {Number} extraMargin margin from the element to view.
 * As per design it should scroll to the element leaving 15px at the top.
 * keeping extraMargin as '0' the default value. as other journeys are using this function.
 *
 * @description
 *
 * How we scroll into view depends on priority rules:
 *
 * 1. Element is already entirely in view: don't scroll.
 * 2. Element will not fit within a single page: Scroll so top of element is at top of page
 * 3. Element will fit within a single page:
 *    3a. Top of the element is in view, bottom is not: scroll so bottom is at bottom of page
 *    3b. Bottom of element is in view, top is not: scroll so top is at top of page
 *    3c. None of the element is in view, element is above scroll: scroll top of element to top of page
 *    3d. None of the element is in view, element is below the scroll: scroll bottom of element to bottom of page
 */
export function scrollElementIntoFullView(elementOrIdOrClass, extraMargin = 0) {
  const smoothScroll = new SmoothScroll('[data-easing="easeInOutQuad"]', {
    easing: 'easeInOutQuad',
  });
  function scrollElementIntoFullViewImpl() {
    const el =
      typeof elementOrIdOrClass === 'string'
        ? document.getElementById(elementOrIdOrClass) ||
          document.getElementsByClassName(elementOrIdOrClass)[0]
        : elementOrIdOrClass;

    if (el?.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const windowHeight = window.innerHeight;
    const windowScrollTop = window.pageYOffset;
    const windowScrollBottom = windowScrollTop + windowHeight;
    const elHeight = el.offsetHeight;
    let elTop = 0;
    let offsetParent = el;

    do {
      elTop += offsetParent.offsetTop;
      offsetParent = offsetParent.offsetParent;
    } while (offsetParent !== null);

    const elBottom = elTop + elHeight;

    // 1. Element is already entirely in view: don't scroll.
    if (windowScrollTop <= elTop && elBottom <= windowScrollBottom) {
      smoothScroll.animateScroll(elTop - extraMargin);
      return;
    }

    // 2. Element will not fit within a single page: Scroll so top of element is at top of page
    if (elHeight > windowHeight) {
      smoothScroll.animateScroll(elTop - extraMargin);
      return;
    }

    // 3a. Top of the element is in view, bottom is not: scroll so bottom is at bottom of page
    if (windowScrollTop <= elTop && elTop <= windowScrollBottom) {
      smoothScroll.animateScroll(elBottom - windowHeight - extraMargin);
      return;
    }

    // 3b. Bottom of element is in view, top is not: scroll so top is at top of page
    if (windowScrollTop <= elBottom && elBottom <= windowScrollBottom) {
      smoothScroll.animateScroll(elTop - extraMargin);
      return;
    }

    // 3c. None of the element is in view, element is above scroll: scroll top of element to top of page
    if (windowScrollTop > elBottom) {
      smoothScroll.animateScroll(elTop - extraMargin);
      return;
    }

    // 3d. None of the element is in view, element is below the scroll: scroll bottom of element to bottom of page
    if (windowScrollBottom < elTop) {
      smoothScroll.animateScroll(elBottom - windowHeight - extraMargin);
    }
  }
  window.requestAnimationFrame(scrollElementIntoFullViewImpl);
}

const dynamicallyLoadedJavaScriptFiles = {};
export function loadJavaScriptFile(href) {
  if (dynamicallyLoadedJavaScriptFiles[href]) {
    return;
  }

  const el = document.createElement('script');
  el.src = href;
  el.async = true;
  document.body.appendChild(el);
  dynamicallyLoadedJavaScriptFiles[href] = true;
}

export const setCookie = (name, value, exdays) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expiration = 'expires=' + d.toUTCString();

  document.cookie =
    name + '=' + value + ';' + expiration + ';domain=.troweprice.com;path=/';
};

export const getCookie = (name) => {
  // will return an array of cookie values given a cookie name
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  let value = '';
  cookieArray.forEach((cookie) => {
    const pairs = cookie.split('=');
    if (
      name.toString().toLowerCase().trim() ===
      pairs[0].toString().toLowerCase().trim()
    ) {
      [value] = pairs.slice(1);
    }
  });
  return value;
};

export function totalOffset(element) {
  let el = element;
  let top = 0;
  let left = 0;
  while (el !== null) {
    top += el.offsetTop;
    left += el.offsetLeft;
    el = el.offsetParent;
  }
  return { top, left };
}

/**
 * Positions an element relative to another, like a tooltip.
 *
 * @param {HTMLElement} popupEl HTML Element to position
 * @param {HTMLElement|Event} relativeTo HTML Element to position the tooltip over OR
 *                                       event with mouse coordinates to use for positioning
 * @param {object} [offset] Object with coordinates to offset the tooltip based on relative element/event
 * @param {number} offset.x Pixels to offset X position
 * @param {number} offset.y Pixels to offset Y position
 * @param {"POPUP_POSITION_TOP"|"POPUP_POSITION_BOTTOM"="POPUP_POSITION_TOP"} position Where to place the
 * tooltip, top or bottom of relative element, default is top
 * @param {string} [adjustedClassName] CSS class name to add to the element if it's position is adjusted to fit on
 * screen and not the optimal position
 */
// Note: ESLINT rules require placing this code after referenced function totalOffset
export function positionPopUp(
  popupEl: HTMLElement,
  relativeTo: Element | MouseEvent,
  position: PopUpPosition,
  offset: DOMRect,
  adjustedClassName: string
) {
  const isTop = position === PopUpPosition.TOP;
  const popUpWidth = popupEl.offsetWidth;
  const popUpHeight = popupEl.offsetHeight;
  const popUpHalfWidth = popUpWidth / 2;
  const winWidth = window.innerWidth;
  const winHeight = window.innerHeight;
  const parentOffset = totalOffset(popupEl.offsetParent);
  let popUpTop: number;
  let popUpLeft: number;
  let adjusted = false; // if we adjust to position whole popUp on screen, don't show arrows

  // The relativeTo prop is either a mouse event or a DOM element and is used as the positioning
  // basis for the popup. If it is a mouse event, then the mouse coordinates embedded in the event
  // are the popup's base position. If it is an element. Then the popup base position is at the
  // center of the element, and the top of the element if the popup is positioned above, or
  // bottom of the element if the popup is below the element.
  if (isMouseEvent(relativeTo)) {
    popUpTop = relativeTo.screenY;
    popUpLeft = relativeTo.screenX;
  } else if (isElement(relativeTo)) {
    const bounds = relativeTo.getBoundingClientRect();
    popUpTop = isTop ? bounds.top : bounds.bottom;
    popUpLeft = bounds.left + (bounds.right - bounds.left) / 2;
  } else {
    // invalid relative to, hidden by default, so won't show
    return;
  }

  // Now center on the base position and adjusted by 10 pixels vertically either
  // up or down depending on placement is top or bottom.
  popUpLeft -= popUpHalfWidth;
  popUpTop = isTop
    ? popUpTop - popUpHeight - 10 // 10px padding
    : popUpTop + 10;

  // If an offset object is provided, then those offsets are added to the popup's now calculated
  // top and left values.
  if (offset.top) {
    popUpTop += offset.top;
  }
  if (offset.left) {
    popUpLeft += offset.left;
  }

  // Finally we check if the popup is off screen. If it is off screen, we adjust it to show on
  // screen with some small extra padding and we add the `adjusted` css class which is used to
  // indicate to css that the position is not default, it is adjusted. The practical implication
  // of this is that we don't add the little arrows when the position is adjusted.
  if (popUpLeft - popUpHalfWidth < 0) {
    popUpLeft = 5;
    adjusted = true;
  } else if (popUpLeft + popUpWidth > winWidth) {
    popUpLeft = winWidth - popUpWidth - 5;
    adjusted = true;
  }

  if (isTop && popUpTop < 0) {
    popUpTop = 10;
    adjusted = true;
  } else if (!isTop && popUpTop + popUpHeight > winHeight) {
    popUpTop = winHeight - popUpHeight - 10;
    adjusted = true;
  }

  // Now we have the final position of the popup within the screen, so we
  // need to adjust that to be based on the document position (offset by
  // scroll and the popup display's parent hierarchy offset)

  // In Safari, the scrolling element for the page is the body, whereas it's supposed
  // to be the root document itself. So for Safari we need to use document.scrollingElement
  // to get the scroll position, which will work in Chrome and Firefox too. IE doesn't
  // support this property, so we need to fall back to documentElement for IE.
  popUpTop += coalesce(
    document.scrollingElement,
    document.documentElement
  ).scrollTop;
  popUpTop -= parentOffset.top;
  popUpLeft -= parentOffset.left;

  const s = popupEl.style;
  s.left = popUpLeft + 'px';
  s.top = popUpTop + 'px';
  s.visibility = 'visible';

  // Pass 'adjusted' class to CSS to know whether or not to suppress the triangles
  if (adjustedClassName) {
    if (adjusted) {
      popupEl.classList.add(adjustedClassName);
    } else {
      popupEl.classList.remove(adjustedClassName);
    }
  }
}
